
import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { CoachProfile } from "@/types/coach";

export const useCoachProfile = () => {
  const [coachProfile, setCoachProfile] = useState<CoachProfile | null>(null);
  const [isCoach, setIsCoach] = useState(false);
  const { toast } = useToast();

  const checkCoachStatus = useCallback(async () => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        throw userError;
      }

      if (!user) {
        return { isCoach: false, coachProfile: null };
      }

      const { data: coach, error: coachError } = await supabase
        .from("coaches")
        .select("id, user_id, coach_name, contact_email, certification, specialization, is_verified, created_at, updated_at")
        .eq("user_id", user.id)
        .eq("is_active", true)
        .maybeSingle();

      if (coachError) {
        throw coachError;
      }

      const profileExists = !!coach;
      setIsCoach(profileExists);
      setCoachProfile(coach);

      return { isCoach: profileExists, coachProfile: coach };
    } catch (error: any) {
      console.error("Erreur dans checkCoachStatus:", error);
      
      toast({
        variant: "destructive",
        title: "Erreur",
        description: `Impossible de charger le profil coach: ${error?.message || "Erreur inconnue"}`,
      });

      return { isCoach: false, coachProfile: null };
    }
  }, [toast]);

  const becomeCoach = useCallback(async (coachData: any) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      // Vérifier s'il existe un profil coach inactif pour cet utilisateur
      const { data: existingCoach } = await supabase
        .from("coaches")
        .select("id")
        .eq("user_id", user.id)
        .eq("is_active", false)
        .maybeSingle();

      let coach;
      let error;

      if (existingCoach) {
        // Réactiver le profil existant avec les nouvelles données
        const result = await supabase
          .from("coaches")
          .update({
            coach_name: coachData.coach_name,
            contact_email: coachData.contact_email,
            certification: coachData.certification,
            specialization: coachData.specialization,
            is_active: true,
            updated_at: new Date().toISOString(),
          })
          .eq("id", existingCoach.id)
          .select()
          .single();
        
        coach = result.data;
        error = result.error;
      } else {
        // Créer un nouveau profil
        const result = await supabase
          .from("coaches")
          .insert({
            user_id: user.id,
            coach_name: coachData.coach_name,
            contact_email: coachData.contact_email,
            certification: coachData.certification,
            specialization: coachData.specialization,
            is_active: true,
          })
          .select()
          .single();
        
        coach = result.data;
        error = result.error;
      }

      if (error) {
        throw error;
      }

      setIsCoach(true);
      setCoachProfile(coach);
      
      toast({
        title: "Succès",
        description: existingCoach ? "Profil coach réactivé avec succès !" : "Profil coach créé avec succès !",
      });

      return coach;
    } catch (error: any) {
      console.error("Erreur becomeCoach:", error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message || "Erreur lors de la création du profil coach",
      });
      throw error;
    }
  }, [toast]);

  const deleteCoachProfile = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      // Désactiver le profil coach (soft-delete) au lieu de supprimer
      const { error } = await supabase
        .from("coaches")
        .update({ is_active: false })
        .eq("user_id", user.id);

      if (error) {
        throw error;
      }

      setIsCoach(false);
      setCoachProfile(null);
      
      toast({
        title: "Succès",
        description: "Profil coach désactivé avec succès",
      });

      return true;
    } catch (error: any) {
      console.error("Erreur deleteCoachProfile:", error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message || "Erreur lors de la désactivation du profil coach",
      });
      throw error;
    }
  }, [toast]);

  return {
    coachProfile,
    isCoach,
    checkCoachStatus,
    becomeCoach,
    deleteCoachProfile,
  };
};
